<script setup lang="ts">
import type { FiltersAccordionProps } from '.';

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from '@/components/ui/accordion';
import { cn } from '@/utils';
import { screenSizesInPixels } from '~~/utils';

import {
	filtersAccordionContentVariants,
	filtersAccordionTitleVariants,
	filtersAccordionTriggerVariants
} from './variants';

const props = withDefaults(defineProps<FiltersAccordionProps>(), {
	variant: 'primary'
});

const triggerClasses = computed(() =>
	filtersAccordionTriggerVariants({ variant: props.variant })
);

const contentClasses = computed(() =>
	filtersAccordionContentVariants({ variant: props.variant })
);

const titleClasses = computed(() =>
	filtersAccordionTitleVariants({ variant: props.variant })
);

const accordionId = uniqueId('filter-accordion-');

const accordionValue = shallowRef<string>('');

const breakpoints = useBreakpoints(screenSizesInPixels);

const isMobile = computed<boolean>(() => breakpoints.active().value === 'sm');

watch(
	() => props.initializeOpen,
	shouldInitializeOpen => {
		if (shouldInitializeOpen) {
			accordionValue.value = accordionId;
		}
	},
	{ immediate: true }
);
</script>

<template>
	<Accordion
		v-model="accordionValue"
		type="single"
		class="w-full"
		:collapsible="!isMobile"
	>
		<AccordionItem :value="accordionId" class="border-0">
			<AccordionTrigger :class="cn(triggerClasses, triggerClass)">
				<span :class="titleClasses">
					{{ title }}
				</span>
			</AccordionTrigger>

			<AccordionContent :class="contentClasses">
				<slot />
			</AccordionContent>
		</AccordionItem>
	</Accordion>
</template>
